import { Locale } from 'date-fns';
import * as dateFnsLocales from 'date-fns/locale';
import { useEffect, useState } from 'react';
import { registerLocale, setDefaultLocale } from 'react-datepicker';

import { LOCAL_STORAGE } from '@/constants';
import { logger } from '@/services/logger';

const defaultLocale = 'en-US';

const localeCatalog = dateFnsLocales as Record<string, Locale>;

const toDateFnsLocale = (locale: string) => locale.replace(/_/g, '-');

const toExportName = (code: string) =>
    code.replace(/[-_]([a-zA-Z])/g, (_, letter: string) => letter.toUpperCase());

const isDateFnsLocale = (value: unknown): value is Locale =>
    !!value && typeof value === 'object' && typeof (value as Locale).localize === 'object';

const lookupLocale = (code: string): Locale | undefined => {
    const match = localeCatalog[toExportName(code)];
    return isDateFnsLocale(match) ? match : undefined;
};

const resolveDateFnsLocale = (appLocale: string): Locale => {
    const normalized = toDateFnsLocale(appLocale);
    const exact = lookupLocale(normalized);
    if (exact) {
        return exact;
    }

    const language = normalized.split('-')[0];
    const byLanguage = lookupLocale(language);
    if (byLanguage) {
        return byLanguage;
    }

    return localeCatalog.enUS;
};

/**
 * Get the current app language from localStorage or HTML lang attribute
 */
const getAppLanguage = (): string => {
    const storedLanguage = localStorage.getItem(LOCAL_STORAGE.LANGUAGE_KEY);
    if (storedLanguage) return toDateFnsLocale(storedLanguage);

    const htmlTag = document.getElementsByTagName('html')[0] as HTMLElement;
    const fullLanguageKey = htmlTag.getAttribute('lang') as string;
    return fullLanguageKey ? toDateFnsLocale(fullLanguageKey.slice(0, 2)) : defaultLocale;
};

const applyLocale = (appLocale: string) => {
    const locale = resolveDateFnsLocale(appLocale);
    registerLocale(appLocale, locale);
    setDefaultLocale(appLocale);
    logger.debug(`Registered date-fns locale ${locale.code} for ${appLocale}`);
};

const useFormattedDate = () => {
    const [currentLocale, setCurrentLocale] = useState<string>(getAppLanguage());

    applyLocale(currentLocale);

    useEffect(() => {
        const handleLanguageChange = () => {
            const newLanguage = getAppLanguage();
            setCurrentLocale(previous => {
                if (newLanguage === previous) {
                    return previous;
                }
                logger.debug(`Language changed from ${previous} to ${newLanguage}, reloading date-fns locale`);
                return newLanguage;
            });
        };

        globalThis.addEventListener('storage', handleLanguageChange);

        const observer = new MutationObserver(handleLanguageChange);
        const htmlNode = document.getElementsByTagName('html')[0] as HTMLElement;
        observer.observe(htmlNode, { attributes: true, attributeFilter: ['lang'] });

        return () => {
            globalThis.removeEventListener('storage', handleLanguageChange);
            observer.disconnect();
        };
    }, []);

    return { isLocaleLoaded: true, currentLocale };
};

export { useFormattedDate };
